import React, { useCallback, useEffect, useState } from 'react';
import { Tabs, Checkbox, Button, message, Card, Row, Col, Typography } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { rolePermissionsApi } from '../services/api-services';

const { Text } = Typography;

const ROLE_LABELS: Record<string, string> = {
  super_admin: '超级管理员',
  admin: '管理员',
  app_admin: '应用管理员',
  user: '普通用户',
};

const MENU_LABELS: Record<string, string> = {
  dashboard: '仪表盘',
  applications: '应用列表',
  employees: '数字人',
  skills: 'Skill 管理',
  tools: 'Tool 管理',
  'mcp-servers': 'MCP Server',
  documents: '文档管理',
  experts: '人工专家',
  tickets: '工单管理',
  products: '产物管理',
  'user-management': '用户管理',
  'role-management': '角色管理',
  'dictionary-management': '字典管理',
  'agent-config': '智能体管理',
  'system-config': '系统配置',
};

const ACTION_GROUPS: { label: string; actions: { key: string; label: string }[] }[] = [
  {
    label: '数字人',
    actions: [
      { key: 'employee:create', label: '创建' },
      { key: 'employee:edit', label: '编辑' },
      { key: 'employee:delete', label: '删除' },
      { key: 'employee:install', label: '安装' },
    ],
  },
  {
    label: 'Skill',
    actions: [
      { key: 'skill:create', label: '创建' },
      { key: 'skill:edit', label: '编辑' },
      { key: 'skill:delete', label: '删除' },
    ],
  },
  {
    label: 'Tool',
    actions: [
      { key: 'tool:create', label: '创建' },
      { key: 'tool:edit', label: '编辑' },
      { key: 'tool:delete', label: '删除' },
    ],
  },
  {
    label: '应用',
    actions: [
      { key: 'application:create', label: '创建' },
      { key: 'application:edit', label: '编辑' },
      { key: 'application:delete', label: '删除' },
    ],
  },
  {
    label: '工单',
    actions: [
      { key: 'ticket:create', label: '创建' },
      { key: 'ticket:evaluate', label: '评价' },
      { key: 'ticket:cancel', label: '取消' },
    ],
  },
  {
    label: '用户',
    actions: [
      { key: 'user:create', label: '创建' },
      { key: 'user:edit', label: '编辑' },
      { key: 'user:delete', label: '删除' },
    ],
  },
  {
    label: '字典',
    actions: [
      { key: 'dictionary:create', label: '创建' },
      { key: 'dictionary:edit', label: '编辑' },
      { key: 'dictionary:delete', label: '删除' },
    ],
  },
];

interface RolePerms {
  menus: string[];
  actions: string[];
}

const RolePanel: React.FC<{ role: string; allMenus: string[] }> = ({ role, allMenus }) => {
  const [perms, setPerms] = useState<RolePerms>({ menus: [], actions: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPerms = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await rolePermissionsApi.get(role);
      setPerms(res.permissions || { menus: [], actions: [] });
    } catch {
      message.error('加载权限失败');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => { loadPerms(); }, [loadPerms]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await rolePermissionsApi.update(role, perms);
      message.success('保存成功');
    } catch {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const toggleMenu = (menu: string, checked: boolean) => {
    setPerms((prev) => ({
      ...prev,
      menus: checked ? [...prev.menus, menu] : prev.menus.filter((m) => m !== menu),
    }));
  };

  const toggleAllMenus = (checked: boolean) => {
    setPerms((prev) => ({ ...prev, menus: checked ? [...allMenus] : [] }));
  };

  const toggleAction = (action: string, checked: boolean) => {
    setPerms((prev) => ({
      ...prev,
      actions: checked ? [...prev.actions, action] : prev.actions.filter((a) => a !== action),
    }));
  };

  const toggleGroupActions = (group: typeof ACTION_GROUPS[0], checked: boolean) => {
    const keys = group.actions.map((a) => a.key);
    setPerms((prev) => ({
      ...prev,
      actions: checked
        ? Array.from(new Set([...prev.actions, ...keys]))
        : prev.actions.filter((a) => !keys.includes(a)),
    }));
  };

  const allMenusChecked = allMenus.every((m) => perms.menus.includes(m));
  const someMenusChecked = perms.menus.length > 0 && !allMenusChecked;

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          保存
        </Button>
      </div>

      <Row gutter={24}>
        <Col span={10}>
          <Card title="菜单权限" size="small" loading={loading}>
            <div style={{ marginBottom: 8 }}>
              <Checkbox
                checked={allMenusChecked}
                indeterminate={someMenusChecked}
                onChange={(e) => toggleAllMenus(e.target.checked)}
              >
                <Text strong>全选</Text>
              </Checkbox>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 24 }}>
              {allMenus.map((menu) => (
                <Checkbox
                  key={menu}
                  checked={perms.menus.includes(menu)}
                  onChange={(e) => toggleMenu(menu, e.target.checked)}
                >
                  {MENU_LABELS[menu] || menu}
                </Checkbox>
              ))}
            </div>
          </Card>
        </Col>
        <Col span={14}>
          <Card title="操作权限" size="small" loading={loading}>
            {ACTION_GROUPS.map((group) => {
              const allChecked = group.actions.every((a) => perms.actions.includes(a.key));
              const someChecked = group.actions.some((a) => perms.actions.includes(a.key)) && !allChecked;
              return (
                <div key={group.label} style={{ marginBottom: 12 }}>
                  <Checkbox
                    checked={allChecked}
                    indeterminate={someChecked}
                    onChange={(e) => toggleGroupActions(group, e.target.checked)}
                  >
                    <Text strong>{group.label}</Text>
                  </Checkbox>
                  <div style={{ paddingLeft: 24, marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {group.actions.map((action) => (
                      <Checkbox
                        key={action.key}
                        checked={perms.actions.includes(action.key)}
                        onChange={(e) => toggleAction(action.key, e.target.checked)}
                      >
                        {action.label}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              );
            })}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const RoleManagement: React.FC = () => {
  const [allMenus, setAllMenus] = useState<string[]>([]);

  useEffect(() => {
    rolePermissionsApi.meta().then((res: any) => {
      setAllMenus(res.menus || []);
    }).catch(() => {});
  }, []);

  const roles = ['super_admin', 'admin', 'app_admin', 'user'];

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">角色管理</h1>
      </div>
      <Tabs
        defaultActiveKey="super_admin"
        items={roles.map((role) => ({
          key: role,
          label: ROLE_LABELS[role] || role,
          children: <RolePanel role={role} allMenus={allMenus} />,
        }))}
      />
    </div>
  );
};

export default RoleManagement;
